/**
 * SRES · AI Report Generation — Stage 12 (wizard step `ai_narratives`).
 *
 * Feb 2026 · Iter76 · Home for the 27-chapter narrative pipeline.
 * Reuses `NarrativeJobsPanel` so start / resume / progress stays in
 * cache-sync with the Final Review page (Stage 13).
 */
import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { Sparkle, ArrowRight, CheckCircle, Warning } from "@phosphor-icons/react";
import { useNavigate } from "react-router-dom";

import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import NarrativeJobsPanel from "@/pages/wizard/NarrativeJobsPanel";

export default function AiReportGenerationStep({ projectId }) {
  const nav = useNavigate();

  const statusQ = useQuery({
    queryKey: ["narrative-status", projectId],
    queryFn:  () => api.get(`/projects/${projectId}/narratives/status`).then((r) => r.data),
    enabled:  !!projectId,
    refetchInterval: 10_000,
  });

  const totals = statusQ.data || {};
  const byStatus = totals.by_status || {};
  const total    = totals.total_paragraphs || 0;
  const drafted  = (byStatus.draft || 0) + (byStatus.ai_generated || 0)
                 + (byStatus.cp_qp_review_required || 0);
  const approved = byStatus.cp_qp_approved || 0;
  const rejected = byStatus.rejected || 0;
  const pending  = Math.max(0, total - approved - rejected);

  return (
    <div className="max-w-6xl space-y-4" data-testid="ai-report-generation-step">
      {/* Hero card */}
      <section className="rounded-lg border border-slate-200 bg-white overflow-hidden">
        <header className="px-4 py-3 border-b border-slate-100 flex items-center gap-2">
          <Sparkle size={16} weight="fill" className="text-brand-blue" />
          <div>
            <div className="font-heading font-bold text-[14px] text-slate-900">
              AI Technical Write-Ups
            </div>
            <div className="text-[11px] text-slate-500">
              27-chapter Phase-C narrative · Claude Sonnet 4.6 · evidence-linked drafts
            </div>
          </div>
        </header>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 p-4">
          <StatCard label="Total paragraphs" value={total}
                          tone="slate" testId="arg-metric-total" />
          <StatCard label="AI-drafted"       value={drafted}
                          tone="brand" testId="arg-metric-drafted" />
          <StatCard label="CP/QP approved"   value={approved}
                          tone="emerald" testId="arg-metric-approved" />
          <StatCard label="Rejected"          value={rejected}
                          tone="rose" testId="arg-metric-rejected" />
        </div>
        <div className="px-4 py-3 border-t border-slate-100 flex items-center gap-2 flex-wrap">
          <div className="text-[11.5px] text-slate-600">
            {pending === 0
                ? "All paragraphs decided — proceed to the Final Review stage to publish."
                : `${pending} paragraph${pending === 1 ? "" : "s"} awaiting CP/QP decision.`}
          </div>
          <div className="flex-1" />
          <Button
            size="sm" variant="outline"
            onClick={() => nav(`/app/projects/${projectId}/wizard/final_review`)}
            data-testid="arg-goto-final-review"
            className="text-[11.5px] gap-1">
            Go to Final Review <ArrowRight size={12} weight="bold" />
          </Button>
        </div>
      </section>

      {/* Shared jobs panel — same cache key as Final Review */}
      <NarrativeJobsPanel
        projectId={projectId}
        title="AI generation jobs"
        showStartButton={true}
        showOnlyMissingToggle={true}
        testIdPrefix="arg" />

      {/* Guidance / help */}
      <section className="rounded-lg border border-slate-200 bg-white p-4">
        <div className="flex items-center gap-2 mb-1.5">
          <Warning size={14} weight="fill" className="text-amber-500" />
          <div className="font-heading font-bold text-[13px] text-slate-900">
            How it works
          </div>
        </div>
        <ul className="text-[12px] text-slate-600 space-y-1 list-disc list-inside">
          <li><span className="font-semibold">Draft missing</span> runs the
              27-chapter DAG for chapters that aren't yet drafted. Recommended
              for the first pass and for resuming after new data lands.</li>
          <li><span className="font-semibold">Regenerate all</span> forces the
              full pipeline — use only when core inputs (density, cut-off,
              composites) have changed materially.</li>
          <li>Interrupted runs (from a backend restart, network hiccup, or
              a browser tab close) show a <em>Resume</em> action — resumption
              is idempotent because <code>only_missing=true</code> skips any
              chapter that already reached <code>draft</code> or
              <code>approved</code>.</li>
          <li>The signature workflow lives on the <strong>Final Review</strong>
              stage — every generated paragraph awaits explicit CP/QP sign-off
              before it enters a published report.</li>
        </ul>
      </section>
    </div>
  );
}

function StatCard({ label, value, tone, testId }) {
  const bg = {
    emerald: "bg-emerald-50 text-emerald-700 border-emerald-200",
    rose:    "bg-rose-50    text-rose-700    border-rose-200",
    brand:   "bg-brand-blue/10 text-brand-blue border-brand-blue/20",
    slate:   "bg-slate-50   text-slate-700   border-slate-200",
  }[tone] || "bg-slate-50 text-slate-700 border-slate-200";
  return (
    <div className={`rounded-md border p-2.5 ${bg}`}
              data-testid={testId}>
      <div className="text-[10px] uppercase tracking-widest opacity-70">{label}</div>
      <div className="font-mono font-bold text-[20px] mt-1">
        {(value ?? 0).toLocaleString()}
      </div>
    </div>
  );
}
